// The event editor's fields (3a R4) and their request body (`EventInput`). Pure: the composer keeps an
// `EventDraft` next to its title / caption / items and turns it into the create / update body here.
import type { EventInfo, EventInput } from "../../api/content";

export interface EventDraft {
  /** `YYYY-MM-DD` and `HH:mm`, wall-clock in `timezone`. */
  date: string;
  time: string;
  /** Optional end time. */
  endTime: string;
  /**
   * The end's date, `YYYY-MM-DD`. Empty = not chosen: the start date, or the next day when the end time is not
   * after the start (an event across midnight). An edit sets it from the stored end, so a multi-day event keeps it.
   */
  endDate: string;
  /** IANA zone: the browser's for a new event, the event's own when editing. */
  timezone: string;
  /** Euros as typed ("45,00"); empty = free. */
  price: string;
  ticketUrl: string;
  live: boolean;
  /** A pasted profile link; empty = me (the author). */
  joinMeUrl: string;
  placeName: string;
  /**
   * The stored map point (3b picker): a place name is only sent with one. A passed event's view carries no
   * location (room-stc sweeps the location row once the event has passed), so editing one sends none: nothing
   * is lost that the server still had.
   */
  coords: { lat: number; lng: number } | null;
  /** ISO 4217; the stored one on edit (never forced back to EUR). */
  currency: string;
  /**
   * An edit's stored instants and their wall-clock form: an untouched start / end is sent back exactly as stored
   * (seconds included), not rebuilt from the minutes the inputs show.
   */
  stored?: { startsAt: string; endsAt: string | null; start: string; end: string | null };
}

export type EventField = "start" | "end" | "price" | "ticket" | "joinMe";

export const CURRENCY = "EUR";

export const browserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid";

export function newEventDraft(timezone: string = browserTimezone()): EventDraft {
  return {
    date: "", time: "", endTime: "", endDate: "", timezone, price: "", ticketUrl: "", live: false, joinMeUrl: "", placeName: "", coords: null,
    currency: CURRENCY,
  };
}

/** `${origin}/profile/{id}`: the profile link room-stc accepts for Join me. */
export const profileLinkOf = (origin: string, userId: string): string => `${origin}/profile/${userId}`;

/**
 * An edit's fields. Join me someone else → their profile link, which is sent back unchanged (an empty link
 * would reset Join me to the author).
 */
export function eventDraftOf(e: EventInfo, authorId: string, origin: string): EventDraft {
  const start = zonedParts(e.startsAt, e.timezone);
  const end = e.endsAt ? zonedParts(e.endsAt, e.timezone) : null;
  return {
    date: start.date,
    time: start.time,
    endTime: end?.time ?? "",
    // Same day as the start: left unchosen, so a new end time before the start still rolls over midnight.
    endDate: end && end.date !== start.date ? end.date : "",
    timezone: e.timezone,
    price: e.priceCents == null ? "" : (e.priceCents / 100).toFixed(2),
    ticketUrl: e.ticketUrl ?? "",
    live: e.live,
    joinMeUrl: e.joinMe.id === authorId ? "" : profileLinkOf(origin, e.joinMe.id),
    placeName: e.location?.placeName ?? "",
    // Null for a passed event (its location row is already swept): see `coords`.
    coords: e.location ? { lat: e.location.lat, lng: e.location.lng } : null,
    currency: e.currency || CURRENCY,
    stored: { startsAt: e.startsAt, endsAt: e.endsAt, start: `${start.date}T${start.time}`, end: end ? `${end.date}T${end.time}` : null },
  };
}

const PARTS = (tz: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** The wall-clock date and time of an instant in a zone. */
export function zonedParts(iso: string, tz: string): { date: string; time: string } {
  const p = Object.fromEntries(PARTS(tz).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** The zone's offset from UTC at an instant, in ms (wall clock − UTC). */
function offsetAt(ms: number, tz: string): number {
  const { date, time } = zonedParts(new Date(ms).toISOString(), tz);
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  return Date.UTC(y, mo - 1, d, h, mi) - Math.floor(ms / 60000) * 60000;
}

/** The UTC instant (ISO) of a wall-clock date and time in a zone. */
export function zonedToIso(date: string, time: string, tz: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  let ms = wall - offsetAt(wall, tz);
  // Near a DST change the offset at the guess may differ from the offset at the answer.
  const again = wall - offsetAt(ms, tz);
  if (again !== ms) ms = again;
  return new Date(ms).toISOString();
}

/** Euros as typed → cents; empty = null (free); anything else = undefined (invalid). */
export function priceCentsOf(text: string): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+([.,]\d{1,2})?$/.test(t)) return undefined;
  const [whole, frac = ""] = t.replace(",", ".").split(".");
  // Over 9 digits of euros, or past int32 cents, does not fit the server's priceCents (Android parsePrice).
  if (whole.replace(/^0+/, "").length > MAX_EURO_DIGITS) return undefined;
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  return cents > MAX_PRICE_CENTS ? undefined : cents;
}

const MAX_EURO_DIGITS = 9;
const MAX_PRICE_CENTS = 2147483647;

/** room-stc takes http(s) ticket links with a host. */
function isWebUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && u.host !== "";
  } catch {
    return false;
  }
}

/** A `/profile/{ref}` link. Scheme and host are the server's call (PROFILE_LINK_HOSTS → INVALID_FIELD). */
export function isProfileLink(s: string): boolean {
  try {
    return /^\/profile\/[^/]+$/.test(new URL(s.trim()).pathname);
  } catch {
    return false;
  }
}

/** Which fields keep Post disabled: a missing start, or a non-empty price / ticket / Join me link that cannot be sent. */
export function eventErrors(d: EventDraft): Partial<Record<EventField, true>> {
  const out: Partial<Record<EventField, true>> = {};
  if (d.date === "" || d.time === "") out.start = true;
  // A chosen end date must give an end after the start (an unchosen one rolls over midnight by itself).
  else if (d.endTime !== "" && d.endDate !== "") {
    const { startsAt, endsAt } = instantsOf(d);
    if (endsAt != null && endsAt <= startsAt) out.end = true;
  }
  if (priceCentsOf(d.price) === undefined) out.price = true;
  if (d.ticketUrl.trim() !== "" && !isWebUrl(d.ticketUrl.trim())) out.ticket = true;
  if (d.joinMeUrl.trim() !== "" && !isProfileLink(d.joinMeUrl)) out.joinMe = true;
  return out;
}

const addDays = (date: string, n: number): string => {
  const [y, mo, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, d + n)).toISOString().slice(0, 10);
};
const daysBetween = (a: string, b: string): number => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

/** The end date the end time falls on: the chosen one, else the start date (or the next day across midnight). */
export function effectiveEndDate(d: EventDraft): string {
  if (d.endDate !== "") return d.endDate;
  if (d.date === "" || d.time === "" || d.endTime === "") return d.date;
  return d.endTime > d.time ? d.date : addDays(d.date, 1);
}

/** A new start date; a chosen end date moves with it, so the event keeps its length in days. */
export function startDatePatch(d: EventDraft, date: string): Partial<EventDraft> {
  if (d.endDate === "" || d.date === "" || date === "") return { date };
  return { date, endDate: addDays(d.endDate, daysBetween(d.date, date)) };
}

function instantsOf(d: EventDraft): { startsAt: string; endsAt: string | null } {
  const s = d.stored;
  const startsAt = s && `${d.date}T${d.time}` === s.start ? s.startsAt : zonedToIso(d.date, d.time, d.timezone);
  if (d.endTime === "") return { startsAt, endsAt: null };
  const endDate = effectiveEndDate(d);
  const endsAt = s && s.end === `${endDate}T${d.endTime}` && s.endsAt ? s.endsAt : zonedToIso(endDate, d.endTime, d.timezone);
  return { startsAt, endsAt };
}

/** The create / update `event` body (call only when `eventErrors` is empty). */
export function toEventInput(d: EventDraft): EventInput {
  const { startsAt, endsAt } = instantsOf(d);
  const ticket = d.ticketUrl.trim();
  return {
    startsAt, endsAt, timezone: d.timezone, priceCents: priceCentsOf(d.price) ?? null, currency: d.currency || CURRENCY,
    ticketUrl: ticket === "" ? null : ticket, live: d.live, joinMeProfileUrl: d.joinMeUrl.trim(),
    ...(d.coords ? { location: { ...d.coords, placeName: d.placeName.trim() } } : {}),
  };
}
